import { useEffect, useMemo } from 'react';
import { Dropdown } from '../Dropdown';
import { ProgressBar } from '../ProgressBar';
import { ExtendedColumn } from './types';

type TableCellProps = {
	row: any;
	column: ExtendedColumn;
	data: any[];
	onChange: (value: string) => void;
};

export default function TableCell({ row, column, data, onChange }: TableCellProps) {
	const value = useMemo(() => {
		//* Below exactMatch prevent potentialValue to return a value before the exact match could be found
		//* Because find method loop one by one array data
		//* Example:
		//* Available values are ['abc', 'ab'] and initialValue is 'ab';
		//* If allowMismatch is true, 'abc' will be returned instead of 'ab' because it's in the second position

		const prop = column.error?.type === 'mismatch' && column.error.target ? column.error.target : column.prop;
		const initialValue = row[prop];

		if (column.type !== 'select') {
			return initialValue;
		}

		const values = typeof column.values === 'function' ? column.values(row) : column.values;
		if (!values) return null;

		//* First find is there is an exact match
		const exactMatch = values.find((v) => {
			//* Case when v (available value) is from type: {label: string; value: any}
			if (typeof v === 'object') {
				return v.value === initialValue;
			}

			return v === initialValue;
		});

		if (exactMatch) {
			if (typeof exactMatch === 'object' && !!exactMatch) return exactMatch.value;
			return exactMatch ?? null;
		}

		const potentialValue = values.find((v) => {
			//* Case when v (available value) is from type: {label: string; value: any}
			if (typeof v === 'object') {
				return `${v.value}`.toLowerCase().includes(`${initialValue}`.toLowerCase());
			}

			return `${v}`.toLowerCase().includes(`${initialValue}`.toLowerCase());
		});

		if (typeof potentialValue === 'object' && !!potentialValue) return potentialValue.value;
		return potentialValue ?? null;
	}, [column, row]);

	useEffect(() => {
		const prop = column.error?.type === 'mismatch' && column.error.target ? column.error.target : column.prop;
		const initialValue = row[prop];

		if (initialValue !== value) {
			onChange(value);
		}
	}, [column, onChange, row, value]);

	const warning = useMemo(() => {
		//* Nullable
		if (!(column.allowNull ?? true) && (!value || `${value}`.length === 0)) {
			return 'Valeur requise';
		}

		//* Unique
		if (column.unique ?? false) {
			const prop = column.error?.type === 'mismatch' && column.error.target ? column.error.target : column.prop;
			let values = [];

			if (column.allowNull ?? true) {
				if (!value || `${value}`.length === 0) return undefined;

				values = data.map((x) => x[prop]).filter((x) => x || `${x}`.length > 0);
			} else {
				values = data.map((x) => x[prop]);
			}

			let sameValues = 0;

			if (typeof column.unique === 'boolean' || column.unique === 'case') {
				sameValues = values.filter((x) => x === value).length;
			} else if (column.unique === 'base') {
				sameValues = values.filter((x) => `${x}`.toLowerCase() === `${value}`.toLowerCase()).length;
			}

			if (sameValues > 1) {
				return `Cette valeur doit être unique (trouvée ${sameValues} fois)`;
			}
		}

		//* Custom validation
		if (typeof column.validate === 'function') {
			const result = column.validate(value);

			//* Result treated as a string
			if (typeof result === 'string') {
				return result;
			}

			//* As boolean
			if (result) {
				return 'Cette valeur ne respecte pas les conditions de validation';
			}
		}

		return undefined;
	}, [column, data, value]);

	const columnType = useMemo(() => {
		if (column.type === 'auto') {
			if (!value) {
				return 'string';
			}

			if (typeof value === 'number') {
				return 'number';
			}

			if (typeof value === 'boolean') {
				return 'checkbox';
			}

			if (value.match(/(true|false)/i)) {
				return 'checkbox';
			}
			if (!isNaN(parseFloat(value))) {
				return 'number';
			}

			return 'string';
		}

		if (column.type === 'auto-non-numeric') {
			if (!value) return 'string';

			if (typeof value === 'number') {
				return 'string';
			}

			if (typeof value === 'boolean') {
				return 'checkbox';
			}

			if (value.match(/(true|false)/i)) {
				return 'checkbox';
			}

			return 'string';
		}

		return column.type;
	}, [column.type, value]);

	if (columnType === 'gauge') {
		return (
			<td>
				<ProgressBar
					id={column.prop}
					value={value}
					min={column.min}
					max={column.max}
					color={column.color}
					displayText={column.displayText}
				/>
			</td>
		);
	}

	if (column.editable ?? true) {
		if (columnType === 'select') {
			let values: { label: string; value: string }[];
			if (typeof column.values === 'function') {
				values = column.values(row);
			} else if (!column.values) {
				values = [];
			} else if (typeof column.values[0] === 'string') {
				values = (column.values as string[]).map((x) => ({
					label: x,
					value: x,
				}));
			} else {
				values = column.values as { label: string; value: string }[];
			}

			return (
				<td
					className={warning && 'warn'}
					title={warning}
				>
					<Dropdown
						name={column.prop}
						title='-'
						defaultValue={value}
						onChange={onChange}
						values={values}
						legacyDropdown
					/>
				</td>
			);
		}

		if (columnType === 'checkbox') {
			return (
				<td
					className={warning && 'warn'}
					title={warning}
				>
					<input
						name={column.prop}
						aria-label={column.label}
						type={columnType}
						className='cell-input'
						defaultChecked={value}
						autoComplete='off'
						required
						onChange={(ev) => void onChange(ev.target.value)}
					/>
				</td>
			);
		}

		return (
			<td
				className={warning && 'warn'}
				title={warning}
			>
				<input
					name={column.prop}
					aria-label={column.label}
					type={columnType}
					className='cell-input'
					defaultValue={value}
					autoComplete='off'
					required
					onChange={(ev) => void onChange(ev.target.value)}
				/>
			</td>
		);
	}

	if (columnType === 'checkbox') {
		return (
			<td
				className={warning && 'warn'}
				title={warning}
			>
				<input
					name={column.prop}
					aria-label={column.label}
					type={columnType}
					className='cell-input'
					checked={value}
					autoComplete='off'
					readOnly
					required
				/>
			</td>
		);
	}

	return (
		<td
			className={warning && 'warn'}
			title={warning}
		>
			{value}
		</td>
	);
}